import { useEffectiveUser } from "@client/hooks/use-effective-user";
import { useUnderlyingRoundPhases } from "@client/hooks/use-round-phase";
import {
  useImpersonateContext,
  useRoundAdmin,
} from "@client/ImpersonateContext";
import {
  roundUsesTracks,
  roundAllowsAnonymization,
} from "@engine/round/accessors";
import {
  adminNavItemIsActive,
  buildRoundAdminOrFunderNavItems,
  isRoundAdminNavItemVisible,
} from "@client/utils/roundAdminNav";
import {
  Sidebar,
  SidebarProvider,
  SidebarContent,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuButton,
  SidebarSeparator,
  SidebarTrigger,
  useSidebar,
} from "@sfc/ui";
import { Fragment, type ReactNode } from "react";
import { RestoredScrollPane } from "@client/components/utils/RestoredScrollPane";
import { Link, useLocation } from "wouter";

export function RoundAdminNavbar({
  roundId,
  children,
}: {
  roundId: string;
  children: ReactNode;
}) {
  return (
    <SidebarProvider className="min-h-0 flex-1 overflow-hidden">
      <RoundAdminSidebar roundId={roundId}>{children}</RoundAdminSidebar>
    </SidebarProvider>
  );
}

function RoundAdminSidebar({
  roundId,
  children,
}: {
  roundId: string;
  children: ReactNode;
}) {
  const [pathname] = useLocation();
  const { open, isMobile, openMobile, setOpenMobile } = useSidebar();
  const currentPhases = useUnderlyingRoundPhases();
  const { isAdmin, isFunder } = useRoundAdmin();
  const { data: viewer } = useEffectiveUser();
  const { value } = useImpersonateContext();

  const visibleItems = buildRoundAdminOrFunderNavItems(roundId, {
    routing: "nested",
    isSfcRound: value?.round.isSfcRound ?? true,
    usesTracks: value ? roundUsesTracks(value.round) : true,
    allowsAnonymization: value ? roundAllowsAnonymization(value.round) : true,
  }).filter((item) =>
    isRoundAdminNavItemVisible(item, {
      isAdmin,
      isSfcAdmin: viewer?.isSfcAdmin,
      isFunder,
      currentPhases,
    }),
  );

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden [--sidebar-width-icon:3.75rem]">
      <div className="flex shrink-0 items-center gap-2 border-b px-4 py-2 md:hidden">
        <SidebarTrigger
          variant="ghost"
          className="size-11"
          aria-label="Open round settings navigation"
          aria-expanded={openMobile}
        />
        <span className="text-sm">Round settings</span>
      </div>
      <div className="relative flex min-h-0 flex-1 overflow-hidden">
        <Sidebar collapsible="icon" className="absolute h-full!">
          <SidebarHeader className="items-end group-data-[collapsible=icon]:items-center">
            <SidebarTrigger
              variant="ghost"
              className="size-11"
              aria-label={
                isMobile
                  ? "Close round settings navigation"
                  : open
                    ? "Collapse round settings navigation"
                    : "Expand round settings navigation"
              }
              aria-expanded={isMobile ? openMobile : open}
            />
          </SidebarHeader>
          <SidebarContent className="group-data-[collapsible=icon]:overflow-y-auto">
            <aside aria-label="Round administration" className="px-2 pb-3">
              <SidebarMenu>
                {visibleItems.map(
                  ({ label, href, icon: Icon, separatorBefore }) => {
                    const isActive = adminNavItemIsActive(pathname, {
                      roundId,
                      linkHref: href,
                      routing: "nested",
                    });
                    return (
                      <Fragment key={href}>
                        {separatorBefore && (
                          <SidebarSeparator className="mx-0 w-full" />
                        )}
                        <SidebarMenuItem>
                          <SidebarMenuButton
                            variant="rail"
                            isActive={isActive}
                            tooltip={label}
                            className="min-h-11 group-data-[collapsible=icon]:size-11! group-data-[collapsible=icon]:justify-center"
                            render={
                              <Link
                                href={href}
                                aria-label={label}
                                aria-current={isActive ? "page" : undefined}
                                onClick={() => {
                                  if (isMobile) {
                                    setOpenMobile(false);
                                  }
                                }}
                              />
                            }
                          >
                            <Icon />
                            <span className="group-data-[collapsible=icon]:hidden">
                              {label}
                            </span>
                          </SidebarMenuButton>
                        </SidebarMenuItem>
                      </Fragment>
                    );
                  },
                )}
              </SidebarMenu>
            </aside>
          </SidebarContent>
        </Sidebar>
        <RestoredScrollPane roundId={roundId}>{children}</RestoredScrollPane>
      </div>
    </div>
  );
}
